<template lang="pug">
div(
    class="flex"
    :class="alignmentClasses"
)
    UniversalLink(
        :link="data.linkUrl"
        :openNewWindow="data.linkTarget === 'NEW_WINDOW'"
    )
        Picture(v-bind="pictureBindings" v-on="pictureEvents")
</template>

<script>
import Picture from '@/components/picture'
import UniversalLink from '@/components/universalLink'
import { getMaxWidth, generateFlexAlignmentClasses } from '../../helpers'

export default {
    name: 'ImageWidget',
    components: {
        UniversalLink,
        Picture,
    },
    inject: ['$story', '$lightbox'],
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
        spaceLeft: {
            type: Object,
            default() {
                return {}
            },
        },
        forceLazyLoad: {
            type: Boolean,
            default: false,
        },
        isImportant: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            shouldLazyLoad: false,
            lightboxIndex: null,
        }
    },
    computed: {
        // TODO: here to prep for inherited links, don't forget about lightboxes
        linkUrl() {
            if (this.data.isInLightbox === 'true') return // Disable Links if Lightbox
            if (this.data.linkUrl) return this.data.linkUrl
            return false
        },
        pictureEvents() {
            const events = {}
            // Add a click event if this image is in a lightbox
            if (this.data.isInLightbox === 'true') {
                events.click = () => {
                    this.$lightbox.open(this.lightboxIndex)
                }
            }
            return events
        },
        // TODO: Try to debounce, typing in max widths generates tons of useless thumbs :B
        pictureBindings() {
            // Get the src, Width, and Height
            // this.getSvgData() is Deprecated but remains for backward compatibility
            const imageData = this.data.svgImage?.filename ? this.getSvgData() : this.getFileData()

            return {
                isLazy: this.shouldLazyLoad,
                isImportant: this.isImportant,
                class: { 'cursor-pointer': this.data.isInLightbox === 'true' },
                ...imageData,
            }
        },
        maxWidth() {
            return getMaxWidth(this.data)
        },
        alignmentClasses() {
            return generateFlexAlignmentClasses(
                this.data.mobileAlignment,
                this.data.tabletAlignment,
                this.data.desktopAlignment
            )
        },
    },
    created() {
        this.shouldLazyLoad = this.$story.shouldLazyLoad(this.forceLazyLoad)
    },
    mounted() {
        // Check if this Image is in a lightbox
        if (this.data.isInLightbox === 'true') {
            // Find the image src we want
            let file = null
            if (this.data.desktopImage?.filename) file = this.data.desktopImage.filename
            else if (this.data.tabletImage?.filename) file = this.data.tabletImage.filename
            else if (this.data.mobileImage?.filename) file = this.data.mobileImage.filename

            if (!file) return

            const { src, width, height } = this.$storyblok.getNormalizedAsset(file, 1000)

            // This will add the image to the end of the array
            this.lightboxIndex = this.$lightbox.add(src, width, height)
        }
    },
    methods: {
        /**
         * Deprecated
         * We no longer support SVG's in imageWidget
         * However we had to remain backwards compatible.
         * For current SVG code See: integrations/storyblok/_components/widgets/svgWidget.vue
         */
        getSvgData() {
            // Early out if we do not have a width/height
            if (!this.data.svgSize.width || !this.data.svgSize.height) return {}

            const svgOptions = {
                width: this.data.svgSize.width,
                height: this.data.svgSize.height,
            }

            const { width: mobileWidth, height: mobileHeight } = this.$storyblok.getNormalizedAsset(
                { src: this.data.svgImage.filename, ...svgOptions },
                this.maxWidth.mobile || this.spaceLeft.mobile
            )

            const { width: tabletWidth, height: tabletHeight } = this.$storyblok.getNormalizedAsset(
                { src: this.data.svgImage.filename, ...svgOptions },
                this.maxWidth.tablet || this.spaceLeft.tablet
            )

            const { width: desktopWidth, height: desktopHeight } =
                this.$storyblok.getNormalizedAsset(
                    { src: this.data.svgImage.filename, ...svgOptions },
                    this.maxWidth.desktop || this.spaceLeft.desktop
                )
            // The Source does not change, so we will always have all 3 versions to return.
            return {
                mobileSrc: this.$storyblok.formatSrc(this.data.svgImage.filename),
                mobileWidth,
                mobileHeight,

                tabletSrc: this.$storyblok.formatSrc(this.data.svgImage.filename),
                tabletWidth,
                tabletHeight,

                desktopSrc: this.$storyblok.formatSrc(this.data.svgImage.filename),
                desktopWidth,
                desktopHeight,
            }
        },
        getFileData() {
            const sources = {
                altText: null,
                mobile: null,
                tablet: null,
                desktop: null,
            }

            if (this.data.mobileImage.filename) {
                sources.altText = this.data.mobileImage.alt
                sources.mobile = this.data.mobileImage.filename
                sources.tablet = this.data.mobileImage.filename
                sources.desktop = this.data.mobileImage.filename
            }

            if (this.data.tabletImage.filename) {
                sources.altText = sources.altText || this.data.tabletImage.alt
                sources.tablet = this.data.tabletImage.filename
                sources.desktop = this.data.tabletImage.filename
            }

            if (this.data.desktopImage.filename) {
                sources.altText = sources.altText || this.data.desktopImage.alt
                sources.desktop = this.data.desktopImage.filename
            }

            const assetOptions = {}

            if (
                this.data.fillSize &&
                !isNaN(parseInt(this.data.fillSize.width)) &&
                !isNaN(parseInt(this.data.fillSize.height))
            ) {
                assetOptions.fillWithWhite = true
                assetOptions.width = this.data.fillSize.width
                assetOptions.height = this.data.fillSize.height
            }

            const {
                src: mobileSrc,
                width: mobileWidth,
                height: mobileHeight,
            } = this.$storyblok.getNormalizedAsset(
                sources.mobile,
                this.maxWidth.mobile || this.spaceLeft.mobile,
                assetOptions
            )

            const {
                src: tabletSrc,
                width: tabletWidth,
                height: tabletHeight,
            } = this.$storyblok.getNormalizedAsset(
                sources.tablet,
                this.maxWidth.tablet || this.spaceLeft.tablet,
                assetOptions
            )

            const {
                src: desktopSrc,
                width: desktopWidth,
                height: desktopHeight,
            } = this.$storyblok.getNormalizedAsset(
                sources.desktop,
                this.maxWidth.desktop || this.spaceLeft.desktop,
                assetOptions
            )

            // Only return the ones that exist
            return {
                alt: sources.altText,
                ...(mobileSrc && {
                    mobileSrc,
                    mobileWidth,
                    mobileHeight,
                }),
                ...(tabletSrc && {
                    tabletSrc,
                    tabletWidth,
                    tabletHeight,
                }),
                ...(desktopSrc && {
                    desktopSrc,
                    desktopWidth,
                    desktopHeight,
                }),
            }
        },
    },
}
</script>
